/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { inflect } from "./string";

type FilterState = Record<string, unknown>;

export type ExcludeFilterMap<T extends FilterState> = Partial<
  Record<Extract<keyof T, string>, Extract<keyof T, string>>
>;

type FilterSidebarState = {
  helperText: string;
  isApplyDisabled: boolean;
  isClearDisabled: boolean;
};

type LivePreviewFilterSidebarFooterState = {
  effectiveResultsCount: number;
  helperTextOverride?: string;
};

export function normalizeFilterState<T extends FilterState>(
  value: T,
  excludeFilterMap: ExcludeFilterMap<T>
): Record<keyof T, unknown> {
  const hasMeaningfulSelection = (stateValue: unknown) =>
    Array.isArray(stateValue)
      ? stateValue.length > 0
      : stateValue !== undefined && stateValue !== false && stateValue !== "";

  return Object.fromEntries(
    Object.entries(value).map(([key, stateValue]) => [
      key,
      (() => {
        if (Array.isArray(stateValue) && stateValue.length === 0) {
          return undefined;
        }

        if (stateValue === false || stateValue === "") {
          return undefined;
        }

        if (typeof stateValue === "boolean") {
          const relatedKey = excludeFilterMap[key as Extract<keyof T, string>];

          if (!relatedKey) {
            return key.startsWith("exclude") ? undefined : stateValue;
          }

          const relatedValue = value[relatedKey];
          return hasMeaningfulSelection(relatedValue) ? stateValue : undefined;
        }

        return stateValue;
      })(),
    ])
  ) as Record<keyof T, unknown>;
}

export function hasActiveFilters<T extends FilterState>(
  value: T,
  excludeFilterMap: ExcludeFilterMap<T>
): boolean {
  const normalizedState = normalizeFilterState(value, excludeFilterMap);

  return Object.values(normalizedState).some(
    (stateValue) =>
      stateValue !== undefined && stateValue !== false && stateValue !== ""
  );
}

export function hasFilterChanges<T extends FilterState>(
  currentValue: T,
  appliedValue: T,
  excludeFilterMap: ExcludeFilterMap<T>
): boolean {
  return (
    JSON.stringify(normalizeFilterState(currentValue, excludeFilterMap)) !==
    JSON.stringify(normalizeFilterState(appliedValue, excludeFilterMap))
  );
}

export function getFilterSidebarState<T extends FilterState>({
  currentState,
  appliedState,
  resultsCount,
  entityLabel,
  excludeFilterMap,
}: {
  currentState: T;
  appliedState: T;
  resultsCount: number;
  entityLabel: string;
  excludeFilterMap: ExcludeFilterMap<T>;
}): FilterSidebarState {
  const hasPendingChanges = hasFilterChanges(
    currentState,
    appliedState,
    excludeFilterMap
  );
  const hasAppliedFilters = hasActiveFilters(appliedState, excludeFilterMap);
  const hasDraftFilters = hasActiveFilters(currentState, excludeFilterMap);
  const canClear = hasAppliedFilters || hasDraftFilters;
  const singularEntityLabel = entityLabel.endsWith("s")
    ? entityLabel.slice(0, -1)
    : entityLabel;
  const inflectedEntityLabel = inflect(
    singularEntityLabel,
    entityLabel
  )(resultsCount);

  return {
    helperText:
      hasPendingChanges || hasAppliedFilters
        ? `${resultsCount} ${inflectedEntityLabel} found`
        : `Filter ${entityLabel}`,
    isApplyDisabled: !hasPendingChanges,
    isClearDisabled: !canClear,
  };
}

export function getLivePreviewFilterSidebarFooterState({
  hasPendingChanges,
  isOpen,
  previewLoading,
  previewResultsCount,
  appliedResultsCount,
  entityLabel,
  defaultLabelWhenNoAppliedCount = false,
}: {
  hasPendingChanges: boolean;
  isOpen: boolean;
  previewLoading: boolean;
  previewResultsCount?: number;
  appliedResultsCount?: number;
  entityLabel: string;
  defaultLabelWhenNoAppliedCount?: boolean;
}): LivePreviewFilterSidebarFooterState {
  const effectiveAppliedResultsCount = appliedResultsCount ?? 0;
  const isPreviewLoading =
    hasPendingChanges &&
    isOpen &&
    previewLoading &&
    previewResultsCount === undefined;
  const effectiveResultsCount = hasPendingChanges
    ? previewResultsCount ?? effectiveAppliedResultsCount
    : effectiveAppliedResultsCount;

  if (isPreviewLoading) {
    return {
      effectiveResultsCount,
      helperTextOverride: `Updating ${entityLabel}...`,
    };
  }

  if (
    !hasPendingChanges &&
    appliedResultsCount === undefined &&
    defaultLabelWhenNoAppliedCount
  ) {
    return {
      effectiveResultsCount,
      helperTextOverride: `Filter ${entityLabel}`,
    };
  }

  return { effectiveResultsCount };
}
